import React, { useMemo } from 'react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import type { SystemDesignLossesResponse } from '../../../../services/oneButtonServices/designServices';
import DashboardModal from './DashboardModal';
import './oneButton-css/DesignLossesModal.css';

const LOSS_LABELS: Record<string, string> = {
    SimulationName: 'Simulation',
    ShadingEngineName: 'Shading Engine',
    InverterEfficiency: 'Inverter Efficiency (%)',
    DcToAcRatio: 'DC to AC Ratio',
    WeatherDataset: 'Weather Dataset',
    Mismatch: 'Mismatch (%)',
    Connections: 'Connections (%)',
    LightInducedDegradation: 'Light induced degradation (%)',
    Soiling: 'Soiling (%)',
    Shading: 'Shading (%)',
    Availability: 'Availability (%)',
    Age: 'Age (%)',
    Snow: 'Snow (%)',
    LossesSource: 'Losses Source',
};

function formatValue(v: unknown): string {
    if (v == null) return '—';
    if (typeof v === 'number') {
        return Number.isFinite(v) ? String(v) : '—';
    }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
}

export interface DesignLossesModalProps {
    open: boolean;
    onClose: () => void;
    losses: SystemDesignLossesResponse | null;
}

export const DesignLossesModal: React.FC<DesignLossesModalProps> = ({ open, onClose, losses }) => {
    const theme = useOneButtonStore((s) => s.theme);

    const entries = useMemo<[string, any][]>(() => {
        if (!losses || typeof losses !== 'object') return [];
        // Only include fields we have labels for, and filter out system fields
        const allKeys = Object.keys(LOSS_LABELS);
        return allKeys
            .map((key): [string, any] => [key, (losses as any)[key]])
            .filter(([_, value]) => value !== undefined);
    }, [losses]);

    return (
        <DashboardModal
            open={open}
            title="Design losses"
            onClose={onClose}
            width={760}
            centered
            maskClosable={false}
            destroyOnClose
            theme={theme}
            className={`obd-design-losses-modal obd-theme-${theme}`}
            rootClassName={`obd-design-losses-modal-root obd-theme-${theme}`}
        >
            <div className="obd-losses-modal">
                <div className="obd-losses-modal__table">
                    {entries.length === 0 ? (
                        <p className="obd-losses-modal__empty">No losses data available.</p>
                    ) : (
                        entries.map(([key, value]: [string, any]) => (
                            <div key={key} className="obd-losses-modal__row">
                                <span className="obd-losses-modal__label">{LOSS_LABELS[key] ?? key}</span>
                                <span className="obd-losses-modal__value">{formatValue(value)}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </DashboardModal>
    );
};

export default DesignLossesModal;
